"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import QuotationPrint from "@/components/print/QuotationPrint";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import DrawerPanel from "@/components/ui/DrawerPanel";
import QuotationForm from "@/components/forms/QuotationForm";
import { Modal } from "@/components/ui/modal/index";
import { toIsoDateOnly } from "@/lib/dateApi";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import Switch from "@/components/form/switch/Switch";

export default function RmcQuotations() {
  const qc = useQueryClient();
  const printRef = React.useRef(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editQuotation, setEditQuotation] = React.useState(null);
  const [detailId, setDetailId] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("active");
  const isActiveFilter = statusFilter === "active";

  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });
  const { data: rows = [] } = useQuery({
    queryKey: ["quotations", isActiveFilter],
    queryFn: async () => (await rmcClient.listQuotations({ is_active: isActiveFilter })).data,
  });
  const { data: detail, isLoading: isDetailLoading } = useQuery({
    queryKey: ["quotation", detailId],
    queryFn: async () => (await rmcClient.getQuotation(detailId)).data,
    enabled: detailId != null,
  });

  const toggle = useMutation({
    mutationFn: ({ id, v }) => rmcClient.setQuotationActive(id, v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotations"] }),
  });

  const pn = (id) => parties.find((p) => p.id === id)?.name;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6">
      <QuotationPrint ref={printRef} quotation={detail} />
      <div>
        <PageBreadCrumb pageTitle="Quotations" />
      </div>
      <div className="-mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={statusFilter === "active" ? "primary" : "outline"}
            className="h-8 rounded-md px-3 py-1 text-xs shadow-none"
            onClick={() => setStatusFilter("active")}
          >
            Active
          </Button>
          <Button
            size="sm"
            variant={statusFilter === "inactive" ? "primary" : "outline"}
            className="h-8 rounded-md px-3 py-1 text-xs shadow-none"
            onClick={() => setStatusFilter("inactive")}
          >
            Inactive
          </Button>
        </div>
        <Button size="sm" className="h-8 rounded-md px-3 py-1 text-xs shadow-none" onClick={() => setCreateOpen(true)}>
          Create
        </Button>
      </div>
      <DataTable
        columns={[
          { key: "no", header: "Quotation No", render: (r) => r.quotation_no },
          { key: "date", header: "Date", render: (r) => r.date },
          { key: "party", header: "Party", render: (r) => pn(r.party_id) },
          {
            key: "tax",
            header: "Tax Type",
            render: (r) => (r.tax_type === "GST" ? `GST ${r.gst_rate ? `(${r.gst_rate}%)` : ""}` : "Non-GST"),
          },
          {
            key: "valid",
            header: "Valid Until",
            render: (r) => r.valid_until || "—",
          },
          {
            key: "status",
            header: "Status",
            render: (r) => {
              const until = r.valid_until ? toIsoDateOnly(r.valid_until) || String(r.valid_until).slice(0, 10) : "";
              const expired = until && until < today;
              const label = !r.is_active ? "Inactive" : expired ? "Expired" : "Active";
              return label;
            },
          },
          {
            key: "grades",
            header: "Grades covered",
            render: (r) => {
              const count = Array.isArray(r.grades_covered)
                ? r.grades_covered.length
                : (r.grade_rates || []).filter((x) => x.rate > 0).length;
              return `${count} grade${count === 1 ? "" : "s"}`;
            },
          },
          {
            key: "sw",
            header: "Active",
            render: (r) => (
              <div
                role="presentation"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Switch
                  key={`q-${r.id}-${r.is_active}`}
                  defaultChecked={r.is_active}
                  color="status"
                  onChange={(v) => toggle.mutate({ id: r.id, v })}
                />
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={false}
        emptyMessage={statusFilter === "active" ? "No active quotations found." : "No inactive quotations found."}
        onRowClick={(r) => setDetailId(r.id)}
      />
      <DrawerPanel open={createOpen} onClose={() => setCreateOpen(false)} title="New quotation" widthClass="max-w-xl">
        <QuotationForm onDone={() => setCreateOpen(false)} />
      </DrawerPanel>
      <Modal
        isOpen={detailId != null}
        onClose={() => setDetailId(null)}
        className="max-w-3xl w-full"
        showCloseButton={false}
      >
        {isDetailLoading ? (
          <div className="p-6 text-sm text-gray-400">Loading quotation…</div>
        ) : detail ? (
          <div className="space-y-4 text-xs text-gray-300">
            <div className="rounded-lg border border-gray-800 bg-gray-950/70 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-gray-500">Quotation</p>
                  <p className="mt-1 text-lg font-semibold text-white">{detail.quotation_no}</p>
                </div>
                <button
                  type="button"
                  aria-label="Close quotation detail"
                  onClick={() => setDetailId(null)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-white/5 text-gray-400 hover:menu-dropdown-badge-active hover:text-white"
                >
                  ✕
                </button>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => printRef.current?.openPrint?.()}>
                  Print quotation
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditQuotation(detail);
                    setEditOpen(true);
                    setDetailId(null);
                  }}
                >
                  Edit quotation
                </Button>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <p>
                  <span className="text-gray-500">Date:</span> {detail.date || "—"}
                </p>
                <p>
                  <span className="text-gray-500">Valid Until:</span> {detail.valid_until || "—"}
                </p>
                <p>
                  <span className="text-gray-500">Party:</span> {detail.party?.name || pn(detail.party_id) || "—"}
                </p>
                <p>
                  <span className="text-gray-500">Status:</span> {detail.is_active ? "Active" : "Inactive"}
                </p>
                <p>
                  <span className="text-gray-500">Tax Type:</span>{" "}
                  {detail.tax_type === "GST"
                    ? `GST${detail.gst_rate ? ` (${detail.gst_rate}%)` : ""}`
                    : "Non-GST"}
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-800 overflow-hidden">
              <div className="border-b border-gray-800 bg-gray-950/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Grades Covered
              </div>
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-950/60">
                    <th className="px-3 py-2 font-semibold text-gray-500">Grade</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500">Rate (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {(detail.items || []).filter((it) => Number(it.rate) > 0).map((it, idx) => {
                    const g = it.grade;
                    return (
                      <tr key={g?.id ?? idx} className="bg-gray-950/20">
                        <td className="px-3 py-2 text-gray-200">{g?.name || `Grade #${idx + 1}`}</td>
                        <td className="px-3 py-2 text-right text-white">₹ {it.rate}</td>
                      </tr>
                    );
                  })}
                  {(!detail.items || detail.items.filter((it) => Number(it.rate) > 0).length === 0) && (
                    <tr className="bg-gray-950/20">
                      <td className="px-3 py-3 text-gray-500" colSpan={2}>
                        No grade rates available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </Modal>
      <DrawerPanel open={editOpen} onClose={() => setEditOpen(false)} title="Edit quotation" widthClass="max-w-xl">
        <QuotationForm
          quotation={editQuotation}
          onDone={() => {
            setEditOpen(false);
            setEditQuotation(null);
          }}
        />
      </DrawerPanel>
    </div>
  );
}
